const destinations = [
  {
    name: "United Kingdom & Ireland",
    context: "State trade missions to London, Edinburgh, and Dublin; LSU study-abroad programs at Oxford and St Andrews",
    urgency: "Executive and academic travel frequently has 7–14 day lead time",
    icon: "ri-map-pin-2-line",
    flag: "GB",
  },
  {
    name: "Germany & Central Europe",
    context: "LSU engineering programs in Germany; petrochemical vendor meetings in Frankfurt, Munich, and Dusseldorf",
    urgency: "Study-abroad and vendor deadlines are fixed and non-negotiable",
    icon: "ri-map-pin-2-line",
    flag: "DE",
  },
  {
    name: "Caribbean & Mexico",
    context: "Family beach vacations, spring break trips from LSU and Southern, cruise connections from New Orleans",
    urgency: "Last-minute resort bookings and spring break trips happen 2–4 weeks out",
    icon: "ri-map-pin-2-line",
    flag: "BS",
  },
  {
    name: "Middle East & Asia",
    context: "Petrochemical assignments in Qatar, UAE, Saudi Arabia; state trade delegations to Singapore and South Korea",
    urgency: "Business visas and assignment dates are immovable — often 2–4 weeks notice",
    icon: "ri-map-pin-2-line",
    flag: "QA",
  },
  {
    name: "Latin America",
    context: "Agricultural research programs in Brazil, Costa Rica, and Argentina; LSU tropical studies in Panama",
    urgency: "Research program deadlines and field season dates are fixed",
    icon: "ri-map-pin-2-line",
    flag: "BR",
  },
  {
    name: "Canada",
    context: "Niagara Falls family trips, Toronto business connections, Montreal cultural exchanges, fishing trips",
    urgency: "Summer road trips and last-minute bookings peak May through August",
    icon: "ri-map-pin-2-line",
    flag: "CA",
  },
];

const commonSituations = [
  {
    stat: "58%",
    label: "of Baton Rouge expedited requests are study-abroad or petrochemical-related, discovered within 3 weeks of travel",
  },
  {
    stat: "2–4 Weeks",
    label: "average notice Baton Rouge travelers have before a planned international trip or assignment",
  },
  {
    stat: "Sep–Feb",
    label: "peak season when LSU study-abroad deadlines, fall trade missions, and spring petrochemical assignments collide",
  },
  {
    stat: "72 Hours",
    label: "shortest turnaround we have accommodated for an LSU study-abroad program departure",
  },
];

export default function BatonRougeDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Baton Rouge Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from Capital Region clients. Understanding local travel behavior is part of why our document review catches issues before they derail your study-abroad program, petrochemical assignment, or state business trip.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">
                {d.context}
              </p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">
                  {d.urgency}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Louisiana Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across Louisiana with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/new-orleans" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                New Orleans <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/shreveport" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Shreveport <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/states/louisiana" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Louisiana <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}